import React from 'react';

interface SpinnerProps {
    size?: 'xxxsmall' | 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large';
    className?: string;
    style?: React.CSSProperties;
}

const getSizeClasses = (size: SpinnerProps['size']) => {
    switch (size) {
        case 'xxxsmall':
            return 'w-3.5 h-3.5'; // 14px
        case 'xxsmall':
            return 'w-6 h-6'; // 24px (close to 25px)
        case 'xsmall':
            return 'w-12 h-12'; // 48px (close to 50px)
        case 'small':
            return 'w-24 h-24'; // 96px (close to 100px)
        case 'medium':
            return 'w-48 h-48'; // 192px (close to 200px)
        case 'large':
            return 'w-96 h-96'; // 384px (close to 400px)
        default:
            return 'w-48 h-48'; // 192px default
    }
};

const getContainerSize = (size: SpinnerProps['size']) => {
    switch (size) {
        case 'xxxsmall':
            return 'min-h-[14px]';
        case 'xxsmall':
            return 'min-h-[25px]';
        case 'xsmall':
            return 'min-h-[50px]';
        case 'small':
            return 'min-h-[100px]';
        case 'medium':
            return 'min-h-[200px]';
        case 'large':
            return 'min-h-[400px]';
        default:
            return 'min-h-[200px]';
    }
};

const Spinner: React.FC<SpinnerProps> = ({ size = 'medium', className = '', style = {} }) => {
    const sizeClasses = getSizeClasses(size);
    const containerSizeClasses = getContainerSize(size);

    return (
        <div
            className={`flex items-center justify-center ${containerSizeClasses} ${className}`}
            style={style}
        >
            {/* Looping motion can cause nausea and dizziness for users with vestibular
                disorders, and a spinner loops with nothing to look away from. Under reduced
                motion the arc gives way to the full ring: still plainly busy, and a complete
                circle rather than an arc frozen mid-turn, which would read as a stalled
                animation. Same trade as LoadingBar's solid full-width line. */}
            <svg
                className={`animate-spin motion-reduce:animate-none ${sizeClasses}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                style={{ color: 'var(--malachite-text-dimmed)' }}
            >
                <circle
                    className="opacity-25 motion-reduce:opacity-100"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                />
                <path
                    className="opacity-75 motion-reduce:opacity-0"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
            </svg>
        </div>
    );
};

export default Spinner;
